import React, { useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useTable, useFilters, usePagination, useExpanded, useSortBy } from "react-table";
import Checkbox from "@material-ui/core/Checkbox";
import * as R from "ramda";
import { parseColumns, parseVisibleColumnFilters, initialColumnFilter, columnShape } from "./utils";

import Toolbar from "./toolbar";
import Pagination from "./pagination";

import Loader from "../loader";

import "./css/index.scss";
import TableHeader from "./table-header";

const Table = ({
  placeHolder,
  columns,
  data,
  options,
  className,
  loading,
  setSelectedFilters,
  selectedFilters,
  setSearchQuery,
  searchQuery,
  onGenerateCSVClick,
  enableGoogleAnalytics,
  bottomActions,
  onSelectAllChange
}) => {
  const [selectedColumnFilters, setSelectedColumnFilters] = useState(initialColumnFilter(columns));
  const [localCheckAll, setLocalCheckAll] = useState(false);
  const memoizedColumnFilters = useMemo(
    () => parseVisibleColumnFilters(columns, selectedColumnFilters),
    [columns, selectedColumnFilters]
  );
  const memoizedColumns = useMemo(() => parseColumns(columns, selectedColumnFilters), [
    columns,
    selectedColumnFilters
  ]);
  const memoizedData = useMemo(() => data, [data]);
  const ArrayValuesToString = useCallback(
    array =>
      R.dropRepeats(
        array
          .flatMap(x => Object.values(x))
          .map(x => (!Array.isArray(x) && typeof x !== "object" ? `${x}` : ""))
          .filter(x => x)
      ),
    []
  );
  const memoizedSuggestions = useMemo(() => ArrayValuesToString(memoizedData), [
    ArrayValuesToString,
    memoizedData
  ]);
  const {
    pagination,
    rowExpansion,
    filter,
    rowsPerPage,
    hasExpansion,
    expandedRows,
    selectableRows,
    extraToolbarOptions,
    emptyElement,
    toolbar
  } = {
    pagination: true,
    filter: true,
    rowExpansion: true,
    rowsPerPage: [5, 10, 20],
    hasExpansion: true,
    expandedRows: [],
    selectableRows: false,
    emptyElement: "No rows",
    toolbar: true,
    ...options
  };

  const tableConfigObject = [];
  if (filter) tableConfigObject.push(useFilters);
  tableConfigObject.push(useSortBy);
  if (rowExpansion) tableConfigObject.push(useExpanded);
  tableConfigObject.push(usePagination);

  const {
    remotePageIndex,
    setRemotePageIndex,
    remoteSort,
    setRemoteSort,
    remoteDesc,
    setRemoteDesc,
    removeDownloadAction
  } = options;
  const [localPageIndex, setLocalPageIndex] = useState(0);

  const currentPageIndex = remotePageIndex || localPageIndex;
  const setCurrentPageIndex = setRemotePageIndex || setLocalPageIndex;
  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    pageCount,
    gotoPage,
    setPageSize,
    pageIndex,
    pageSize,
    toggleSortBy
  } = useTable(
    {
      columns: memoizedColumns,
      data: memoizedData
    },
    ...tableConfigObject
  );
  useEffect(() => {
    if (currentPageIndex !== pageIndex) {
      gotoPage(currentPageIndex);
    }
  }, [currentPageIndex, pageIndex, gotoPage]);
  useEffect(() => {
    if (remoteSort) {
      toggleSortBy(remoteSort, remoteDesc, false);
    }
    // pasar la funcion produce un bucle infinito
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteSort, remoteDesc]);
  const emptySearch = <span>No hay resultados para la búsqueda realizada</span>;
  const emptyResults = () => {
    if (searchQuery) {
      return emptySearch;
    }
    if (selectedFilters.length > 0 && options.emptyFilteredElement) {
      return options.emptyFilteredElement;
    }
    return emptyElement;
  };
  const findExpandableColumn = x => {
    return x.column.isExpandableColumnInfo;
  };
  return (
    <div className={`${className} cepsa-table`}>
      {toolbar && (
        <Toolbar
          placeHolder={placeHolder}
          onGenerateCSVClick={onGenerateCSVClick}
          columnFilters={memoizedColumnFilters}
          onColumnFilterChange={values => setSelectedColumnFilters([...values])}
          onFilterChange={values => {
            setSelectedFilters([...values]);
            setLocalCheckAll(false);
          }}
          queryChange={query => setSearchQuery(query)}
          extraToolbarOptions={extraToolbarOptions}
          suggestions={memoizedSuggestions}
          activeFilters={selectedFilters}
          data={data}
          columns={columns}
          removeDownloadAction={removeDownloadAction}
          enableGoogleAnalytics={enableGoogleAnalytics}
        />
      )}
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr key={headerGroup.getHeaderGroupProps()} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, i) => {
                if (columns[i].name === "selected" && selectableRows) {
                  return (
                    <th>
                      <Checkbox
                        checked={localCheckAll}
                        edge="start"
                        className="input-check"
                        onChange={e => {
                          onSelectAllChange(e.target.checked);
                          setLocalCheckAll(e.target.checked);
                        }}
                      />
                    </th>
                  );
                }
                return (
                  <>
                    {columns[i].options && columns[i].options.expandedColumn ? (
                      <></>
                    ) : (
                      <TableHeader
                        sort={remoteSort}
                        desc={remoteDesc}
                        setRemoteSort={setRemoteSort}
                        setRemoteDesc={setRemoteDesc}
                        key={columns[i].name}
                        column={column}
                        name={columns[i].label}
                        headerClass={column.headerClass}
                      />
                    )}
                  </>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {(() => {
            if (loading) return <Loader />;
            if (R.isEmpty(page)) {
              return (
                <tr>
                  <td colSpan={columns.length + (selectableRows ? 1 : 0) - (hasExpansion ? 1 : 0)}>
                    {emptyResults()}
                  </td>
                </tr>
              );
            }
            return page.map(
              row =>
                prepareRow(row) || (
                  <>
                    <tr
                      key={`tr_${row}`}
                      {...row.getRowProps()}
                      className={`${row.getRowProps().className} ${
                        expandedRows.includes(row.original.myTableIndex)
                          ? "opened-expanded"
                          : "closed-expanded"
                      }`}
                      style={row.original.disabled ? { pointerEvents: "none", opacity: 0.4 } : {}}
                    >
                      {row.cells.map(cell => {
                        const cellProps = { ...cell.getCellProps() };
                        return (
                          !cell.column.isExpandableColumnInfo && (
                            <td
                              key={`td_${cell.column.id}`}
                              {...cellProps}
                              className={`${cell.column.cellClass} ${cell.column.id || ""}`}
                            >
                              <div className="inline-header">{cell.column.render("Header")}</div>
                              <div style={row.original.error ? { color: "#d52b1e" } : {}}>
                                {cell.render("Cell")}
                              </div>
                            </td>
                          )
                        );
                      })}
                    </tr>
                    {hasExpansion && expandedRows.includes(row.original.myTableIndex) && (
                      <tr className="expanded">
                        <td colSpan={selectableRows ? row.cells.length : row.cells.length - 1}>
                          {row.cells.find(findExpandableColumn) &&
                            row.cells.find(findExpandableColumn).render("Cell")}
                        </td>
                      </tr>
                    )}
                  </>
                )
            );
          })()}
        </tbody>
      </table>
      {bottomActions}
      {pagination && (
        <Pagination
          page={currentPageIndex + 1}
          totalPages={pageCount}
          rowsPerPage={rowsPerPage}
          selectedRowsPerPage={Number(pageSize)}
          setSelectedRowsPerPage={x => setPageSize(Number(x))}
          setPage={x => setCurrentPageIndex(Number(x) - 1)}
        />
      )}
    </div>
  );
};

// To make proptype recursive

Table.propTypes = {
  placeHolder: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape(columnShape)).isRequired,
  // Unknown shape
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.array,
  loading: PropTypes.bool,
  // TODO
  options: PropTypes.shape({
    pagination: PropTypes.bool, // default to true
    filter: PropTypes.bool, // default to true
    weith: PropTypes.number, // default to 1
    rowExpansion: PropTypes.bool, // default to true
    rowsPerPage: PropTypes.arrayOf(PropTypes.number),
    hasExpansion: PropTypes.bool, // default to true
    expandedRows: PropTypes.arrayOf(PropTypes.number),
    selectableRows: PropTypes.bool, // default to false
    extraToolbarOptions: PropTypes.node,
    emptyElement: PropTypes.node,
    emptyFilteredElement: PropTypes.node,
    toolbar: PropTypes.bool,
    remotePageIndex: PropTypes.number,
    setRemotePageIndex: PropTypes.func,
    remoteSort: PropTypes.string,
    setRemoteSort: PropTypes.func,
    remoteDesc: PropTypes.bool,
    setRemoteDesc: PropTypes.func,
    removeDownloadAction: PropTypes.bool
  }),
  selectedFilters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    })
  ),
  searchQuery: PropTypes.string,
  setSelectedFilters: PropTypes.func,
  setSearchQuery: PropTypes.func,
  onGenerateCSVClick: PropTypes.func,
  enableGoogleAnalytics: PropTypes.bool,
  bottomActions: PropTypes.node,
  onSelectAllChange: PropTypes.func
};

Table.defaultProps = {
  title: "",
  className: "",
  options: {},
  data: [],
  loading: false,
  setSelectedFilters: () => ({}),
  setSearchQuery: () => ({}),
  onGenerateCSVClick: () => ({}),
  selectedFilters: [],
  searchQuery: "",
  placeHolder: "Buscar...",
  enableGoogleAnalytics: false,
  bottomActions: null,
  onSelectAllChange: () => ({})
};

export default Table;
